<template>
  <div>
    <va-modal
      class="evollu-form"
      :value="showModal"
      id="appointmentFormModal"
      ref="appointmentFormModal"
      size="large"
      :noOutsideDismiss="true"
      :okText="'users.save' | translate"
      :cancelText="'users.cancel' | translate"
      :dangerText="'references.inputs.delete' | translate"
      :showDelete="appointment && appointment.created_by && appointment.created_by.id === $auth.user().id"
      @ok="submit"
      :okDisabled="isSubmitting || !canSubmitForm"
      @cancel="cancel"
      @remove="remove">

      <div slot="header" class="modal-title">
        {{ getModalTitle() }}
      </div>

      <form class="evollu-form">
        <div class="row" v-if="facilities.length">
          <div class="col">
            <label class="form-label">{{ $t('agenda.appointments.facility') }}</label>
            <multiselect
              v-model="form.facility"
              :options="facilitiesOptions"
              track-by="id"
              label="name"
              :custom-label="nameWithCity"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="isUpdating || facilitiesOptions.length < 2"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
          </div>
        </div>

        <!-- Assign Activity Dropdown -->
        <div class="row">
          <div class="col-12">
            <label class="form-label">{{ $t('clinical.appointments.activity') }}</label>
            <multiselect
              v-model="form.activity"
              :options="activitiesForFacility"
              label="name"
              track-by="id"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="false"
              :disabled="appointmentIsActive || !form.facility || activitiesForFacility.length < 2"
              :multiple="false"
              :searchable="true"
              @input="onActivityForFacilityChanged">
              <template slot="singleLabel" slot-scope="props">
                {{ $t(`clinical.appointments.activity_types.${props.option.id}`, props.option.name) }}
              </template>
              <span slot="noResult">{{ 'patients.noResults'| translate }}</span>
            </multiselect>
            <small v-show="appointmentFormErrors.has('entity_activity')" class="help text-danger">
              {{ appointmentFormErrors.get('entity_activity') | translate }}
            </small>
          </div>
        </div>

        <!-- Assign Dispenser Dropdown -->
        <div class="row">
          <div class="col-12">
            <label class="form-label">{{ $t('clinical.appointments.dispenser') }}</label>
            <multiselect
              v-model="form.user"
              :options="usersForActivityAndFacility"
              label="name"
              :custom-label="createDispenserNameLabel"
              track-by="id"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              open-direction="bottom"
              :allow-empty="allowEmptyUser"
              :disabled="appointmentIsActive || !form.facility || !form.activity || !hasAppointmentPermissions"
              :multiple="false"
              :searchable="true">
              <template slot="option" slot-scope="props">
                <span v-if="props.option.nickname">{{ `${props.option.name} (${props.option.nickname})` }}</span>
                <span v-else>{{ `${props.option.name}` }}</span>
              </template>
              <span slot="noResult">{{ 'patients.noResults'| translate }}</span>
            </multiselect>
            <small v-show="appointmentFormErrors.has('entity_activity_user_id')" class="help text-danger">
              {{ appointmentFormErrors.get('entity_activity_user_id') | translate }}
            </small>
            <small v-show="appointmentFormErrors.has('user_id')" class="help text-danger">
              {{ appointmentFormErrors.get('user_id') | translate }}
            </small>
          </div>
        </div>

        <!-- Assign Appointment Start Date-->
        <div class="row">
          <div class="col-12 col-md-6">
            <label class="form-label">{{ $t('clinical.appointments.start_placeholder') }}</label>
            <DatePicker
              id="date-picker-appointment-start"
              v-model="form.start"
              :config=datePickerConfig
              :clearable="false"
              :disabled="(appointment && appointment.started_at && appointment.user && appointment.user.id === $auth.user().id)"
              iconClass="i-clock"
              @input="appointmentFormErrors.has('start') ? appointmentFormErrors.clear('start') : null"
            >
              <span slot="icon">
                <i class="i-clock"></i>
              </span>
            </DatePicker>
            <small v-show="appointmentFormErrors.has('start')" class="help text-danger">
              {{ appointmentFormErrors.get('start') | translate }}
            </small>
          </div>

          <!-- Assign Appointment Type-->
          <div class="col-12 col-md-6">
            <label class="form-label">{{ $t('clinical.appointments.type') }}</label>
            <multiselect
              v-model="form.type"
              :options="appointmentTypesOptions"
              :customLabel="option => getAppointmentTypeLabel(option)"
              track-by="id"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="!(!isUpdating || (isUpdating && (appointment.type_id === null || (appointment.type_id !== null && userIsCreator))))"
              @input="appointmentFormErrors.clear('type_id')"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
            <small v-show="appointmentFormErrors.has('type_id')" class="help text-danger">
              {{ appointmentFormErrors.get('type_id') | translate }}
            </small>
          </div>
        </div>

        <!-- Marketing Code -->
        <div class="row">
          <div class="col-12 col-md-6">
            <va-input
              id="marketing-code"
              v-model="form.marketing_code"
              :label="$t('clinical.appointments.marketing_code')"
              :placeholder="$t('clinical.appointments.marketing_code')"
              @input="appointmentFormErrors.clear('marketing_code')"
            />

            <small v-show="appointmentFormErrors.has('marketing_code')" class="help text-danger">
              {{ $t(appointmentFormErrors.get('marketing_code')) }}
            </small>
          </div>
        </div>

        <!-- Assign Person Dropdown -->
        <div class="row">
          <div class="col-12">
            <div class="d-flex align-items-center justify-content-start clickable">
              <h5 class="mr-4 mb-0">{{ $t('clinical.appointments.person.label') }}</h5>
              <span v-if="!isUpdating" class="d-flex align-items-center">
                <span v-show="!createNewPerson" @click="toggleNewPerson" class="i-add-bg"></span>
                <h6 v-if="!createNewPerson" @click="toggleNewPerson"
                    class="mb-0 ml-2">{{ $t('agenda.clinical.appointments.add_patient') }}</h6>
                <span v-show="createNewPerson" @click="toggleNewPerson" class="i-search-bg"></span>
                <h6 v-if="createNewPerson" @click="toggleNewPerson"
                    class="mb-0 ml-2">{{ $t('agenda.clinical.appointments.search_patient') }}</h6>
              </span>
            </div>

            <div v-if="!createNewPerson" class="mt-3">
              <multiselect
                v-model="form.person"
                :options="availablePeople"
                track-by="id"
                select-label=""
                selected-label=""
                :loading="isFetchingServer"
                :internal-search="false"
                :clear-on-select="false"
                :close-on-select="true"
                :options-limit="10"
                :limit="3"
                :disabled="isUpdating || createNewPerson || person !== undefined"
                :limit-text="(count) => $t('tables.multiselect_limitText_and') + count + this.$t('tables.multiselect_limitText_others')"
                deselect-label=""
                :allow-empty="true"
                :placeholder="$t('clinical.appointments.search_patient')"
                :multiple="false"
                :searchable="true"
                :show-no-options="false"
                @search-change="searchPerson"
                open-direction="bottom">
                <template slot="placeholder">
                  <div class="d-flex align-items-center">
                    <span class="i-custom-search p-2"></span>&nbsp;
                    {{ $t('clinical.appointments.search_patient') }}
                  </div>
                </template>
                <template slot="option" slot-scope="{option}">
                  <span>{{ `${option.name}` }}</span>
                  <span v-if="option.process_id"> - {{ option.process_id }}</span>
                </template>
                <template slot="singleLabel" slot-scope="{option}">
                  <span class="i-custom-search p-2" v-if="!isUpdating"></span>&nbsp;
                  <span>{{ `${option.first_name}` }}</span>
                  <span v-if="option.last_name"> {{ `${option.last_name}` }}</span>
                </template>
                <span slot="noResult">{{ 'patients.noResults'| translate }}</span>
              </multiselect>
            </div>
          </div>
        </div>

        <div v-if="form.person && !createNewPerson">
          <SelectedPersonInfo class="mx-3 mb-3" :person="form.person"/>
        </div>

        <div class="row" v-if="createNewPerson">
          <h6 class="col mb-3 ml-3">{{ $t('clinical.people.create.title') }}</h6>
          <PersonForm
            class="col-12"
            v-model="form.person"
            key="personForm"
            :editing="true"
            :service="service"
            :formErrors="personFormErrors"/>
        </div>

        <!-- Has Companion -->
        <div class="new-input-design">
          <div class="row">
            <div class="col-12 col-md-3">
              <span class="select is-fullwidth is-small">
                <va-checkbox
                  :label="$t('clinical.appointments.has_companion')"
                  id="checkbox-has-company"
                  :isCircle="true"
                  :disabled="!form.person"
                  v-model="form.has_companion"/>
              </span>
            </div>

            <div class="col-12 col-md-3" v-if="form.has_companion">
              <span class="select is-fullwidth is-small">
                <va-checkbox
                  :label="$t('clinical.appointments.has_companion_clinical_file')"
                  id="checkbox-has-company-with-clinical-file"
                  :isCircle="true"
                  v-model="form.has_companion_person"
                />
              </span>
            </div>
          </div>

          <div class="row" v-if="form.has_companion">

            <!-- Companion Input -->
            <div class="col-12 col-md-9" v-if="!form.has_companion_person">
              <va-input
                :placeholder="$t('clinical.appointments.companion_name')"
                v-model="form.companion"
                :disabled="!form.has_companion"
                id="editing-company"
              />
            </div>

            <!-- Companion Search -->
            <div class="col-12 col-md-9" v-if="form.has_companion && form.has_companion_person">
              <div class="form-group" :class="{'has-error': appointmentFormErrors.has('companion_person_id')}"
                   style="margin-bottom: 1rem;">
                <div class="input-group person-input-group">
                  <multiselect
                    v-model="form.companion_person"
                    :options="availablePeople"
                    track-by="id"
                    select-label=""
                    selected-label=""
                    :loading="isFetchingServer"
                    :internal-search="false"
                    :clear-on-select="false"
                    :close-on-select="true"
                    :options-limit="10"
                    :limit="3"
                    :limit-text="(count) => $t('tables.multiselect_limitText_and') + count + this.$t('tables.multiselect_limitText_others')"
                    deselect-label=""
                    :allow-empty="true"
                    :placeholder="$t('clinical.appointments.companion_name')"
                    :multiple="false"
                    :searchable="true"
                    :show-no-options="false"
                    @search-change="searchPerson"
                    open-direction="bottom">
                    <template slot="placeholder">
                      <div class="d-flex align-items-center">
                        <span class="i-custom-search p-2"></span>&nbsp;
                        {{ $t('clinical.appointments.search_patient') }}
                      </div>
                    </template>
                    <template slot="option" slot-scope="{option}">
                      <span>{{ `${option.name}` }}</span>
                      <span v-if="option.process_id"> - {{ option.process_id }}</span>
                    </template>
                    <template slot="singleLabel" slot-scope="{option}">
                      <span class="i-custom-search p-2" v-if="!isUpdating"></span>&nbsp;
                      <span>{{ `${option.first_name}` }}</span>
                      <span v-if="option.last_name"> {{ `${option.last_name}` }}</span>
                    </template>
                    <span slot="noResult">{{ 'patients.noResults'| translate }}</span>
                  </multiselect>
                  <small v-show="appointmentFormErrors.has('companion_person_id')" class="help text-danger">
                    {{ appointmentFormErrors.get('companion_person_id') | translate }}
                  </small>
                </div>
              </div>
            </div>

            <!--Relation Type-->
            <div class="col-12 col-md-3">
              <div class="form-group" :class="{'has-error': appointmentFormErrors.has('relationship_type_id')}"
                   style="margin-bottom: 1rem;">
                <div class="input-group">
                  <multiselect
                    v-model="form.relationship_type_id"
                    :options="relationshipTypesOptions"
                    :customLabel="option => $t(option.name)"
                    track-by="id"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    :placeholder="$t('clinical.appointments.relationship_type')"
                    :allow-empty="true"
                    :multiple="false"
                    :searchable="true"
                    :disabled="!form.has_companion">
                    <span slot="noResult">{{ $t('patients.noResults') }}</span>
                    <template slot="singleLabel" slot-scope="{option}">
                      <span>{{ $t(option.name) }}</span>
                    </template>
                  </multiselect>
                  <small v-show="appointmentFormErrors.has('relationship_type_id')" class="help text-danger">
                    {{ appointmentFormErrors.get('relationship_type_id') | translate }}
                  </small>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Assign Outcome Dropdown -->
        <div class="row" v-if="isUpdating">
          <div class="col-6">
            <label class="form-label section-label">{{ $t('clinical.appointments.outcome') }}</label>
            <div class="form-group" :class="{'has-error': appointmentFormErrors.has('status_id')}">
              <div class="input-group">
                <multiselect
                  v-model="form.status"
                  :options="appointmentStatusesOptions"
                  :customLabel="option => `${$t(`clinical.people.anamnesis.outcome_translations.${(option.name).toLowerCase()}`)}`"
                  label="name"
                  track-by="id"
                  select-label=""
                  selected-label=""
                  deselect-label=""
                  placeholder="---"
                  open-direction="bottom"
                  :allow-empty="true"
                  :multiple="false"
                  :searchable="true"
                  :disabled="!canUpdateOutcome"
                  @input="onFormStatusInput">
                  <template slot="option" slot-scope="props">
                    <span>{{
                        `${$t(`clinical.people.anamnesis.outcome_translations.${(props.option.name).toLowerCase()}`)}`
                      }}</span>
                  </template>
                  <span slot="noResult">{{ 'patients.noResults'| translate }}</span>
                </multiselect>
                <small v-show="appointmentFormErrors.has('status_id')" class="help text-danger">
                  {{ appointmentFormErrors.get('status_id') | translate }}
                </small>
              </div>
            </div>
          </div>
          <div class="col-6" v-if="appointmentStatusesReasonsOptions.length">
            <label class="form-label section-label">{{ $t('clinical.appointments.outcome_reason') }}</label>
            <div class="form-group" :class="{'has-error': appointmentFormErrors.has('status_reason_id')}">
              <div class="input-group">
                <multiselect
                  v-model="form.status_reason"
                  :options="appointmentStatusesReasonsOptions"
                  :customLabel="option => `${$t(`clinical.people.anamnesis.outcome_translations.${(option.name).toLowerCase()}`)}`"
                  label="name"
                  track-by="id"
                  select-label=""
                  selected-label=""
                  deselect-label=""
                  placeholder="---"
                  open-direction="bottom"
                  :allow-empty="true"
                  :multiple="false"
                  :searchable="true"
                  @input="appointmentFormErrors.clear('status_reason_id')"
                  :disabled="!canUpdateOutcome">
                  <template slot="option" slot-scope="props">
                    <span>{{
                        `${$t(`clinical.people.anamnesis.outcome_translations.${(props.option.name).toLowerCase()}`)}`
                      }}</span>
                  </template>
                  <span slot="noResult">{{ 'patients.noResults'| translate }}</span>
                </multiselect>
                <small v-show="appointmentFormErrors.has('status_reason_id')" class="help text-danger">
                  {{ appointmentFormErrors.get('status_reason_id') | translate }}
                </small>
              </div>
            </div>
          </div>
        </div>

        <!-- Observations Input -->
        <div class="row">
          <div class="col-12">
            <label class="form-label">{{ $t('agenda.inputs.observations') }}</label>
            <va-input
              v-model="form.obs"
              type="textarea"
              :placeholder="$t('clinical.appointments.observations_placeholder')"
              :minRows="3"
              :maxRows="8"/>
          </div>
        </div>
      </form>

    </va-modal>

    <ConfirmationModal
      v-if="showDeletingModal"
      :title="$t('clinical.appointments.actions.delete')"
      :body="$t('clinical.appointments.actions.delete_message')"
      @modal-confirmed="appointmentDeleted"
      @modal-canceled="appointmentDeleteCanceled"/>

  </div>
</template>
<script>
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'
import Multiselect from 'vue-multiselect'
import Errors from '@/utils/StandardErrors.js'
import PersonForm from '@/components/clinical/people/PersonForm'
import SelectedPersonInfo from '@/components/clinical/appointments/SelectedPersonInfo'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import ExpandTransition from '@/components/common/transitions/ExpandTransition'
import { AppointmentService, PersonService } from '@/api/AuthClinicalService'
import DatePicker from '@/components/common/DatePicker'
import ApiService from '@/api/ApiService'
import axios from 'axios'
import { Portuguese as PTLocale } from 'flatpickr/dist/l10n/pt'
import { Spanish as ESLocale } from 'flatpickr/dist/l10n/es'
import { uniqBy, flatten, debounce } from 'lodash'
import { FORM_DEFAULTS, RELATIONSHIP_TYPES } from './config'

PTLocale.firstDayOfWeek = 1
const errorsPrefix = 'clinical.appointments.errors.'
const WriteAccessType = 2
const AppointmentInfoType = 200
const PEOPLE_QUERY_LIMIT = 10
const DispenserFieldId = 8
const DispenserPremisesId = 9

const STATUSES = {
  NO_APPOINTMENT: 1,
  NO_TEST: 2,
  NO_LOSS: 3,
  NO_SELL: 4,
  SELL: 5
}

export default {
  components: {
    PersonForm,
    DatePicker,
    Multiselect,
    FadeTransition,
    SelectedPersonInfo,
    ExpandTransition,
    ConfirmationModal
  },
  props: {
    fromEvent: {
      type: Object,
      required: false
    },
    facilities: {
      type: Array,
      required: true
    },
    person: {
      type: Object,
      required: false
    },
    appointment: {
      type: Object,
      required: false
    },
    service: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      cancelationCallback: null,
      isSubmitting: false,
      canClose: false,
      canCancel: true,
      show: true,
      form: {
        person: null,
        activity: null,
        entity: null,
        facility: null,
        aud_provider: null,
        start: null,
        type: null,
        end: null,
        relationship_type_id: null,
        user: null,
        has_companion: false,
        companion: null,
        status: null,
        status_reason: null,
        has_companion_person: false,
        companion_person: null,
        marketing_code: null
      },
      availablePeople: [],
      availableAppointmentTypes: [],
      availableAppointmentStatuses: [],
      appointmentFormErrors: new Errors(),
      personFormErrors: new Errors(),
      showDeletingModal: false,
      showModal: false,
      isFetchingServer: false,
      showPersonForm: false,
      createNewPerson: false,
      entityActivityUsers: [],
      relationshipTypesOptions: RELATIONSHIP_TYPES,
      appointmentWithAnamnesis: null
    }
  },
  async created () {
    await this.prepareData()
    if (this.isUpdating) {
      this.fillForm()
    } else {
      this.preFillForm()
      this.searchPerson()
    }
  },
  mounted () {
    this.showModal = true
  },
  methods: {
    createDispenserNameLabel: function (user) {
      if (user.nickname) {
        return `${user.name} (${user.nickname})`
      }
      return user.name
    },
    prepareData: function () {
      let promises = [this.loadAppointmentTypes(), this.loadAppointmentStatuses()]

      if (this.isUpdating) {
        this.appointmentWithAnamnesis = JSON.parse(JSON.stringify(this.appointment))
        promises.push(this.loadEntityActivityUsers(this.appointment.start, this.appointment.end))
        promises.push(this.loadAppointmentAnamnesis())
      }

      return Promise.all(promises)
    },
    loadAppointmentAnamnesis: function () {
      return new Promise((resolve, reject) => {
        AppointmentService.get(this.service.url, this.appointment.id)
          .then(appointment => {
            this.appointmentWithAnamnesis.clinical_questionnaires = appointment.clinical_questionnaires
            this.appointmentWithAnamnesis.hearing_devices = appointment.hearing_devices
            this.appointmentWithAnamnesis.life_styles = appointment.life_styles
            this.appointmentWithAnamnesis.otoscopies = appointment.otoscopies
            this.appointmentWithAnamnesis.tonal_audiometries = appointment.tonal_audiometries
            this.appointmentWithAnamnesis.vocal_audiometries = appointment.vocal_audiometries
            resolve(appointment)
          })
          .catch(err => {
            console.log(err)
            reject(err)
          })
      })
    },
    onFormStatusInput: function (status) {
      if (this.form.status_reason) {
        this.form.status_reason = null
      }
      this.appointmentFormErrors.clear('status_id')
    },
    toggleNewPerson: function () {
      if (!this.isUpdating) {
        this.form.person = null
        this.createNewPerson = !this.createNewPerson
      }
    },
    getModalTitle: function () {
      if (this.appointment) {
        return this.$t('clinical.appointments.edit')
      }
      return this.$t('clinical.appointments.create')
    },
    getAppointmentTypeLabel: function (option) {
      let label = option.name
      if (option.slug && option.name !== option.slug) {
        label += ` (${option.slug})`
      }
      return label
    },
    onActivityForFacilityChanged: function (activity) {
      if (this.usersForActivityAndFacility.length) {
        this.form.user = this.usersForActivityAndFacility[0]
      } else {
        this.form.user = null
      }
    },
    loadAppointmentTypes: function () {
      return new Promise((resolve, reject) => {
        AppointmentService.getTypes(this.service.url)
          .then(types => {
            this.availableAppointmentTypes = types
            resolve()
          }).catch(errors => {
            console.log(errors)
            reject(errors)
          })
      })
    },
    loadAppointmentStatuses: function () {
      return new Promise((resolve, reject) => {
        AppointmentService.getStatuses(this.service.url)
          .then(statuses => {
            this.availableAppointmentStatuses = statuses
            resolve()
          }).catch(errors => {
            console.log(errors)
            reject(errors)
          })
      })
    },

    searchPerson: debounce(function (query = '') {
      this.isFetchingServer = true
      PersonService.query(this.service.url, { limit: PEOPLE_QUERY_LIMIT, query })
        .then(people => {
          this.availablePeople = this.form.person ? people.filter(p => p.id !== this.form.person.id) : people
        }).catch(errors => {
          console.log('errors', errors)
        }).finally(() => {
          this.isFetchingServer = false
        })
    }, 500),
    cancel: function () {
      this.showModal = false
      setTimeout(() => {
        this.$emit('modal-closed')
      }, 250)
    },
    fillForm: function () {
      this.form = JSON.parse(JSON.stringify(this.appointment))
      this.form.start = this.form.start.substring(0, 16)
      this.form.facility = this.facilities.find(facility => facility.id === this.appointment.facility.id)
      this.form.relationship_type_id = this.relationshipTypesOptions.find(relation => relation.id === this.appointment.relationship_type_id)
    },
    preFillForm: function () {
      if (this.facilities.length === 1) {
        this.form.facility = this.facilities[0]
      }
      if (!this.fromEvent) {
        let start = moment().add(FORM_DEFAULTS.NEXT_START_INCREMENT, FORM_DEFAULTS.NEXT_START_INTERVAL).startOf(FORM_DEFAULTS.NEXT_START_INTERVAL)
        let end = start.clone().add(FORM_DEFAULTS.START_INCREMENT, FORM_DEFAULTS.INCREMENT_INTERVAL)
        this.form.start = start.format('YYYY-MM-DD HH:mm')
        this.form.end = end.format('YYYY-MM-DD HH:mm')
      } else {
        this.form.start = this.fromEvent.start.substring(0, 16)
        this.form.end = this.fromEvent.end.substring(0, 16)
      }
      if (this.person) {
        this.form.person = this.person
      }

      if (this.has_companion && this.has_companion_person && this.companion_person) {
        this.form.companion_person = this.companion_person
      }
    },
    loadEntityActivityUsers (start, end) {
      return new Promise((resolve, reject) => {
        if (this.cancelationCallback !== null) {
          this.cancelationCallback()
          resolve()
        }
        let params = {
          infoType: AppointmentInfoType,
          accessType: WriteAccessType,
          inclusive: false,
          statusesOfApproval: '1,3',
          activities: `${DispenserFieldId},${DispenserPremisesId}`,
          serviceId: this.service.id,
          start: this.$options.filters.utcTime(start),
          end: this.$options.filters.utcTime(end)
        }

        ApiService.cancelableQuery(`${this.$store.state.evollauth_server}/entities-activities-users`, params, new axios.CancelToken(c => {
          this.cancelationCallback = c
        }))
          .then(response => {
            this.entityActivityUsers = response.data.data
            resolve()
          })
          .catch(errors => {
            if (!axios.isCancel(errors)) {
              console.log(errors)
              reject(errors)
            }
          })
          .finally(() => {
            this.cancelationCallback = null
          })
      })
    },
    nameWithCity ({ name, address }) {
      if (!address || !address.city) return name
      return name + ' (' + address.city + ')'
    },
    submit: function () {
      let data = this.getFormData(this.isUpdating)
      this.isUpdating ? this.update(data) : this.create(data)
    },
    create: function (data) {
      this.isSubmitting = true
      AppointmentService.store(this.service.url, data)
        .then(response => {
          let appointment = response.data.data
          this.showToast(this.$t('clinical.appointments.actions.created'), this.$store.state.successToastOptions)
          this.$emit('appointment-created', { appointment })
          if (response.data.error) {
            setTimeout(() => {
              this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
            }, this.$store.state.successToastOptions.duration)
          }
        }).catch(errors => {
          if (errors.response.status === 422) {
            this.appointmentFormErrors.record(errors.response.data.errors, errorsPrefix)
            this.personFormErrors = this.personFormErrors.record(errors.response.data.errors, 'people.errors.')
            this.personFormErrors = this.personFormErrors.nestedErrors('person.')
            this.showToast(this.$t('clinical.appointments.errors.form_contains_errors'), this.$store.state.errorToastOptions)
          }
          console.log('errors', errors)
        }).finally(() => {
          this.isSubmitting = false
        })
    },
    update: function (data) {
      this.isSubmitting = true
      AppointmentService.update(this.service.url, this.appointment.id, data)
        .then(response => {
          let appointment = response.data.data
          this.showToast(this.$t('clinical.appointments.actions.updated'), this.$store.state.successToastOptions)
          this.canClose = true
          appointment.start = this.$options.filters.localTime(appointment.start)
          appointment.end = this.$options.filters.localTime(appointment.end)
          this.$emit('appointment-updated', { appointment })
          if (response.data.error) {
            setTimeout(() => {
              this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
            }, this.$store.state.successToastOptions.duration)
          }
        }).catch(errors => {
          if (errors.response.status === 422) {
            this.appointmentFormErrors.record(errors.response.data.errors, errorsPrefix)
            this.showToast(this.$t('clinical.appointments.errors.form_contains_errors'), this.$store.state.errorToastOptions)
          }
          console.log('errors', errors)
        }).finally(() => {
          this.isSubmitting = false
        })
    },
    remove: function () {
      if (this.activeAppointment && parseInt(this.activeAppointment.id) === parseInt(this.appointment.id)) {
        this.showToast(this.$t('clinical.appointments.end_before_delete'), this.$store.state.errorToastOptions)
        return
      }
      this.showModal = false
      this.showDeletingModal = true
    },
    appointmentDeleted: function (data) {
      this.isSubmitting = true
      AppointmentService.destroy(this.service.url, this.appointment.id)
        .then((response) => {
          this.showToast(this.$t('clinical.appointments.actions.deleted'), this.$store.state.successToastOptions)
          if (response.data && response.data.error) {
            setTimeout(() => {
              this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
            }, this.$store.state.successToastOptions.duration)
          }
          this.$emit('appointment-deleted', { appointment: this.appointment })
        }).catch(error => {
          console.log(error)
          this.showToast(this.$t('clinical.appointments.actions.server_error'), this.$store.state.errorToastOptions)
        }).finally(() => {
          this.isSubmitting = false
        })
    },
    appointmentDeleteCanceled: function () {
      this.appointmentFormErrors.clear()
      setTimeout(() => {
        this.showDeletingModal = false
        this.showModal = true
      }, 500)
    },
    getFormData (isUpdating = false) {
      let body = {
        start: this.$options.filters.utcTime(this.form.start),
        end: this.$options.filters.utcTime(this.form.end),
        user_id: this.form.user ? this.form.user.id : null,
        activity_id: this.form.activity ? this.form.activity.id : null,
        type_id: this.form.type ? this.form.type.id : null,
        relationship_type_id: this.form.relationship_type_id ? this.form.relationship_type_id.id : null,
        private: !this.form.public,
        obs: this.form.obs,
        has_companion: this.form.has_companion,
        has_companion_person: this.form.has_companion_person,
        companion: this.form.companion,
        companion_person_id: this.form.companion_person ? this.form.companion_person.id : null,
        marketing_code: this.form.marketing_code || null,
        is_updating: isUpdating
      }

      if (this.createNewPerson) {
        body.person = this.getPersonFormData()
      } else {
        body.person_id = this.form.person ? this.form.person.id : null
      }

      if (!this.isUpdating) {
        body.facility_id = this.form.facility ? this.form.facility.id : null
      }

      if (this.canUpdateOutcome) {
        body.status_id = this.form.status ? this.form.status.id : null
        body.status_reason_id = this.form.status_reason ? this.form.status_reason.id : null
      }

      return body
    },
    getPersonFormData: function () {
      let data = JSON.parse(JSON.stringify(this.form.person))
      if (data.entity) {
        data.entity_id = data.entity.id
        delete data.entity
      }
      if (data.phone) {
        data.phone = data.phone.replace(/\s/g, '')
      }
      if (data.gender) {
        data.gender = data.gender.id
      }
      return data
    }
  },
  computed: {
    datePickerConfig: function () {
      let config = {
        enableTime: true,
        time_24hr: true,
        minuteIncrement: 15,
        dateFormat: 'Y-m-d H:i',
        defaultHour: '09'
      }

      if (this.appointment && this.appointment.started_at) {
        config.minDate = this.moment(this.appointment.start).set({ hour: 0, minute: 0 }).format('YYYY-MM-DD HH:MM')
        config.maxDate = this.moment(this.appointment.start).set({ hour: 23, minute: 59 }).format('YYYY-MM-DD HH:MM')
      }

      if (this.$i18n.locale() === 'pt') {
        config.locale = PTLocale
      } else if (this.$i18n.locale() === 'es') {
        config.locale = ESLocale
      } else {
        config.locale = { firstDayOfWeek: 1 }
      }
      return config
    },
    waitingRoomModuleKey: function () {
      return `waiting-room-${this.service.slug}`
    },
    ...mapState({
      activeAppointment (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/activeAppointment`] || null
      }
    }),
    ...mapGetters({
      availableAudProviders: 'availableAudProviders',
    }),
    canUpdateOutcome: function () {
      /* if (!this.isTodayOrBefore) {
        return false
      } */

      if (!this.isUpdating) {
        return true
      }

      if (!this.appointment.status) {
        return true
      }

      return true
    },
    isTodayOrBefore: function () {
      let start = this.moment(this.form.start)

      let startOfDay = this.moment().set({ hour: 0, minute: 0, seconds: 0 })

      return start.diff(startOfDay, 'days') <= 0
    },
    getFormTitle: function () {
      if (this.form.facility) {
        return this.isUpdating ? `${this.$t('clinical.appointments.update')} - ${this.nameWithCity(this.form.facility)}` : `${this.$t('clinical.appointments.create')} - ${this.nameWithCity(this.form.facility)}`
      }
      return this.isUpdating ? `${this.$t('clinical.appointments.update')}` : `${this.$t('clinical.appointments.create')}`
    },
    isUpdating: function () {
      return this.appointment !== undefined && this.appointment !== null
    },
    canSubmitForm: function () {
      let condition = this.form.start && this.form.end && this.form.facility && this.form.person && this.form.activity
      if (this.allowEmptyUser) {
        return condition
      }
      return condition && this.form.user
    },
    facilitiesOptions: function () {
      return uniqBy(flatten(this.entityActivityUsers.map(entityActivityUser => {
        return entityActivityUser.entity.facilities.map(facility => ({
          ...facility,
          entity_id: entityActivityUser.entity.id
        }))
      })), 'id')
    },
    activitiesForFacility: function () {
      if (!this.form.facility) {
        return []
      }
      const activities = this.entityActivityUsers
        .filter(entityActivityUser => {
          return entityActivityUser.entity.facilities.some(facility => facility.id === this.form.facility.id)
        })
        .map(activityUser => {
          return {
            ...activityUser.activity,
            name: this.$t(`clinical.appointments.activity_types.${activityUser.activity.id}`, activityUser.activity.name)
          }
        })
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

      return uniqBy(activities, 'id')
    },
    usersForActivityAndFacility: function () {
      if (!this.form.activity) {
        return []
      }

      return uniqBy(this.entityActivityUsers.filter(activityUser => {
        return activityUser.entity.facilities.some(facility => facility.id === this.form.facility.id) && activityUser.activity.id === this.form.activity.id && activityUser.user
      }).map(activityUser => activityUser.user), 'id')
    },
    allowEmptyUser: function () {
      if (!this.form.activity) {
        return false
      }
      let entityActivityUsers = [
        ...this.entityActivityUsers
      ]
      if (this.form.facility) {
        entityActivityUsers = this.entityActivityUsers.filter(eau => eau.entity.id === this.form.facility.entity_id)
      }
      return entityActivityUsers.some(activityUser => activityUser.activity.id === this.form.activity.id && activityUser.user == null)
    },
    hasAppointmentPermissions: function () {
      return this.service.user_permissions.some(permission => permission.info_type === AppointmentInfoType && permission.access_type >= WriteAccessType)
    },
    appointmentTypesOptions: function () {
      let appointmentTypes = this.availableAppointmentTypes
      /* if (this.form.activity) {
        appointmentTypes = appointmentTypes.filter(appointmentType => {
          return appointmentType.activity_id === this.form.activity.id
        })
      } */
      appointmentTypes = appointmentTypes.filter(app => app.active)
      return appointmentTypes
    },
    appointmentStatusesReasonsOptions: function () {
      if (!this.form.status) {
        return []
      }
      return this.availableAppointmentStatuses.filter(status => status.parent_id === this.form.status.id)
    },
    appointmentStatusesOptions: function () {
      let statuses = this.availableAppointmentStatuses.filter(status => !status.parent_id)

      /* if (this.isTodayOrBefore) {
        statuses = statuses.filter(status => this.appointmentWithAnamnesis.with_sale && this.appointmentWithAnamnesis.hearing_devices ? status.id === STATUSES.SELL : status.id !== STATUSES.SELL)

        if (this.appointmentWithAnamnesis.with_tonal_audiometry && this.appointmentWithAnamnesis.tonal_audiometry) {
          statuses = statuses.filter(status => status.id !== STATUSES.NO_APPOINTMENT && status.id !== STATUSES.NO_TEST)
        }
      } else {
        statuses = statuses.filter(status => status.id === STATUSES.NO_APPOINTMENT)
      } */
      statuses = statuses.filter(status => status.id === STATUSES.NO_APPOINTMENT)
      return statuses
    },
    appointmentIsActive: function () {
      return this.isUpdating &&
        (this.appointment.started_at !== null && this.moment(this.appointment.started_at).isBefore(this.moment())) &&
        (this.appointment.ended_at === null || (this.appointment.ended_at !== null && this.moment(this.appointment.ended_at).isAfter(this.moment())))
    },
    userIsCreator: function () {
      return this.isUpdating &&
        this.appointment.created_by !== null &&
        this.appointment.created_by.id === this.$auth.user().id
    }
  },
  watch: {
    facilitiesOptions: function (facilities) {
      if (facilities.length) {
        if (this.isUpdating) {
          if (this.appointment.facility) {
            this.form.facility = facilities.find(facility => facility.id === this.appointment.facility.id)
          }
        } else {
          let facility = null
          if (this.form.facility !== null) {
            facility = facilities.find(facility => facility.id === this.form.facility.id)
          }
          this.form.facility = facility || facilities[0]
        }
      } else if (this.form.facility) {
        this.form.facility = null
      }
    },
    activitiesForFacility: function (activities) {
      if (activities.length) {
        if (this.form.activity !== null) {
          this.form.activity = activities.find(activity => activity.id === this.form.activity.id) || activities[0]
        } else {
          this.form.activity = activities[0]
        }
      } else if (this.form.activity) {
        this.form.activity = null
      }
    },
    usersForActivityAndFacility: function (users) {
      if (users.length) {
        if (this.form.user !== null) {
          this.form.user = users.find(user => user.id === this.form.user.id) || users[0]
        } else {
          this.form.user = users[0]
        }
      } else if (this.form.user) {
        this.form.user = null
      }
    },
    'form.has_companion': {
      handler: function (hasCompanion) {
        if (!hasCompanion && (this.form.companion || this.form.relationship_type_id)) {
          this.form.companion = null
          this.form.has_companion_person = false
          this.form.companion_person = null
          this.form.relationship_type_id = null
        }
      },
      deep: true
    },
    'form.has_companion_person': {
      handler: function (hasCompanionPerson) {
        if (!hasCompanionPerson && this.form.companion_person) {
          this.form.companion_person = null
          this.form.relationship_type_id = null
        }
      },
      deep: true
    },
    'form.type': function () {
      if (this.form.start) {
        let increment = this.form.type && this.form.type.default_time ? this.form.type.default_time : FORM_DEFAULTS.DEFAULT_APPOINTMENT_DURATION
        this.form.end = moment(this.form.start).add(increment, 'minutes').format('YYYY-MM-DD HH:mm')
      }
    },
    'form.start': {
      handler: function (start) {
        if (start) {
          let increment = this.form.type && this.form.type.default_time ? this.form.type.default_time : FORM_DEFAULTS.DEFAULT_APPOINTMENT_DURATION
          this.form.end = moment(start).add(increment, 'minutes').format('YYYY-MM-DD HH:mm')
        }
      }
    },
    'form.end': {
      handler: function (end) {
        if (end) {
          this.loadEntityActivityUsers(this.form.start, this.form.end)
        }
      }
    },
    appointmentTypesOptions: function (appointmentTypes) {
      if (appointmentTypes.length && this.form.type) {
        this.form.type = appointmentTypes.find(type => type.id === this.form.type.id) || null
      } else {
        this.form.type = null
      }
    },
    createNewPerson: function (option) {
      if (!option && this.form.person) {
        this.form.person = null
      }
    }
  }
}
</script>

<style lang="scss">

span.form-label {
  & i.i-clock {
    background-color: red;
  }
}

.person-input-group {
  & .multiselect__content-wrapper {
    box-shadow: none !important;
  }
}

.form-label-active:hover {
  cursor: pointer;
}

</style>
